/**
 * Phase-5 refinement 5 — duplicate menu rotation detection ACROSS days.
 *
 * For each regular restaurant per side (breakfast / lunch / dinner — Phase 7
 * adds breakfast), the picked `*_menu_name` should be unique across voyage
 * days. If the user picks the same menu name on 2+ days for the same
 * restaurant on the same side, those occurrences are flagged.
 *
 * Scope is per voyage, per regular restaurant, per side. Breakfast / lunch
 * / dinner are independent.
 *
 * Comparison is case-insensitive + trimmed. Empty / "closed" menus are
 * ignored so a sea-day closure doesn't collide with another day's closure
 * as a "duplicate".
 *
 * Return shape (Map for cheap O(1) lookups in the table renderer):
 *
 *   Map<restaurantId, {
 *     breakfast: Map<normalisedName, number[]>,  // Phase 7
 *     lunch:     Map<normalisedName, number[]>,
 *     dinner:    Map<normalisedName, number[]>,
 *   }>
 *
 * Only restaurants/menus with 2+ occurrences are present in the maps.
 */
const SIDES = ["breakfast", "brunch", "lunch", "tea_time", "dinner"];

function _norm(name) {
  return (name || "").trim().toLowerCase();
}

/**
 * Returns true iff the restaurant library doc declares 2+ menus for the
 * given side. Casual venues like the buffet grill that intentionally serve
 * the SAME menu every day (only 1 rotation defined) are excluded — picking
 * the same menu daily for them is correct, not a duplicate to flag.
 *
 * `restaurantsLib` may be null/undefined, in which case the filter is
 * disabled and every restaurant is eligible.
 */
function _restaurantHasRotation(restaurantsLib, restaurantId, side) {
  if (!restaurantsLib) return true; // no lib → don't filter
  const lib = restaurantsLib.find((r) => r.id === restaurantId);
  if (!lib) return true; // unknown restaurant → don't filter
  const menus = lib[`${side}_menus`] || [];
  return menus.length >= 2;
}

export function computeMenuDuplicates(allDays, restaurantsLib) {
  // restaurantId → side → normalisedName → [day_numbers...]
  const counts = new Map();
  if (!Array.isArray(allDays)) return new Map();

  for (const day of allDays) {
    const dayNum = day?.day_number;
    if (!dayNum) continue;
    const rows = (day.meal_plan || {}).regular_restaurants || [];
    for (const row of rows) {
      const rid = row.restaurant_id;
      if (!rid) continue;
      for (const side of SIDES) {
        // Skip closed days — a "closed" panel doesn't have a menu, so
        // collisions on closed days are meaningless.
        if (row[`${side}_status`] === "closed") continue;
        // Skip restaurants whose library only declares a single menu for
        // this side — picking the same menu every day there is the
        // intended behaviour, not a duplicate to flag.
        if (!_restaurantHasRotation(restaurantsLib, rid, side)) continue;
        const name = _norm(row[`${side}_menu_name`]);
        if (!name) continue;
        if (!counts.has(rid)) {
          const init = {};
          for (const s of SIDES) init[s] = new Map();
          counts.set(rid, init);
        }
        const sideMap = counts.get(rid)[side];
        if (!sideMap.has(name)) sideMap.set(name, []);
        sideMap.get(name).push(dayNum);
      }
    }
  }

  // Prune: keep only names with ≥2 occurrences.
  const out = new Map();
  for (const [rid, sides] of counts.entries()) {
    const kept = {};
    for (const s of SIDES) kept[s] = new Map();
    let any = false;
    for (const side of SIDES) {
      for (const [name, days] of sides[side].entries()) {
        if (days.length >= 2) {
          kept[side].set(name, [...days].sort((a, b) => a - b));
          any = true;
        }
      }
    }
    if (any) out.set(rid, kept);
  }
  return out;
}

/**
 * Given the duplicate map + a single day's regular-restaurant row + a
 * side ("lunch" | "dinner") + the current day number, returns either:
 *   - { isDup: false, days: null }
 *   - { isDup: true,  days: [number, ...other day numbers excluding current] }
 *
 * The day list excludes the current day so the tooltip can read
 * "Used on Day 3, Day 7 too" cleanly.
 */
export function isRowMenuDuplicate(map, row, side, currentDayNumber) {
  if (!map || !row) return { isDup: false, days: null };
  const rid = row.restaurant_id;
  if (!rid) return { isDup: false, days: null };
  if (row[`${side}_status`] === "closed")
    return { isDup: false, days: null };
  const name = _norm(row[`${side}_menu_name`]);
  if (!name) return { isDup: false, days: null };
  const sideMap = map.get(rid)?.[side];
  const days = sideMap?.get(name);
  if (!days || days.length < 2) return { isDup: false, days: null };
  const others = days.filter((n) => n !== currentDayNumber);
  return { isDup: true, days: others };
}

/**
 * Set of day numbers that contain at least one duplicate menu rotation.
 * Used by the DayNavigator to render a small red dot indicator.
 */
export function daysWithDuplicateMenus(map) {
  const out = new Set();
  if (!map) return out;
  for (const sides of map.values()) {
    for (const side of SIDES) {
      for (const days of sides[side].values()) {
        days.forEach((n) => out.add(n));
      }
    }
  }
  return out;
}
